import { traverseItems } from "~/components/filter/traverseItems";
import { useVirtualizer } from "@tanstack/vue-virtual";
import type { IToC } from "~/models/IToC";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { MaybeRefOrGetter } from "@vueuse/core";

export function setupRowVirtualizer(
  flatToc: MaybeRefOrGetter<(IToC | ILegalTextParagraph)[]>,
  paragraphs: MaybeRefOrGetter<ILegalTextParagraph[]>,
  parentRef: MaybeRefOrGetter<HTMLElement | null>
) {
  const veryFirstParagraphIndex = useArrayFindIndex(
    flatToc,
    (t) => "paragraph" in t
  );

  const allItems = computed(() => {
    const resolvedParagraphs = toValue(paragraphs);
    const firstP = resolvedParagraphs[0];
    const lastP = resolvedParagraphs[resolvedParagraphs.length - 1];
    if (!firstP || !lastP) {
      return [];
    }

    const resolvedToc = toValue(flatToc);
    const firstPIdx = resolvedToc.findIndex((t) => t.id === firstP.id);
    const lastPIdx = resolvedToc.findIndex((t) => t.id === lastP.id);

    const startOffset =
      firstPIdx === veryFirstParagraphIndex.value ? 0 : firstPIdx;

    return resolvedToc.slice(startOffset, lastPIdx + 1).map((item) => {
      if ("paragraph" in item) {
        return resolvedParagraphs.find((p) => p.id === item.id) ?? item;
      }
      return item;
    });
  });

  const estimateSize = (index: number) => {
    const item = allItems.value[index];
    if (!item) {
      return 0;
    }

    // guess for header with y-padding
    let size = 90;

    if (!("nodes" in item)) {
      return size;
    }

    // make a guesstimate based on the number of characters in the paragraph
    traverseItems(item.nodes, ({ item }) => {
      if (!("text" in item.properties)) {
        return;
      }

      size += Math.ceil(item.properties.text.length / 70) * 45;
    });

    return size;
  };

  const getItemKey = useMemoize((index: number) => {
    const item = allItems.value[index];
    if (!item) {
      return index;
    }
    if ("paragraph" in item) {
      return `p-${item.id}`;
    }
    return `toc-${item.id}`;
  });

  const rowVirtualizer = useVirtualizer(
    computed(() => ({
      count: allItems.value.length,
      getScrollElement: () => toValue(parentRef),
      estimateSize,
      getItemKey,
    }))
  );

  const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());

  const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

  return {
    rowVirtualizer,
    virtualRows,
    totalSize,
    allItems,
  };
}
